// @ts-nocheck
/**
 * Ludipek B2B location selector.
 *
 * Reads the buyer's available CompanyLocations from window.__LUDIPEK_B2B__.companyLocations
 * (rendered by Liquid from customer.company_available_locations). If multiple locations are
 * available, renders a pill inside #b2b-location-selector showing the currently selected city
 * (flag + name), with a transparent native <select> overlaid on top so clicks, keyboard, and
 * mobile pickers keep native behavior. The open dropdown lists every city with the current one
 * selected. If only one location exists, auto-selects it silently (no pill). On change, persists
 * to localStorage, invalidates the price cache, and reloads the page so pricing and stock reflect
 * the new location.
 *
 * Source of truth is Shopify's session-active company location (window.__LUDIPEK_B2B__.currentLocation,
 * from customer.current_location). The native post-login location chooser sets it server-side without
 * touching our localStorage, so initialize() reconciles localStorage to it on every load — this is what
 * makes a native selection actually stick instead of resetting to the first available location.
 *
 * Exposes window.LudipekB2BLocation = { getStoreCode, getLocation, getCurrentNetsuiteCustomerId,
 *   getEffectiveCountry, getEffectiveCurrency }.
 *
 * TODO: group by company when multi-company contacts are real. Today every entry in
 * companyLocations is flattened across companies — a contact attached to two unrelated Companies
 * would see duplicate store_codes in the dropdown with no company context. The data layer
 * already supports it (each entry has its own netsuite_customer_id); the selector UX is the gap.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  const STORAGE_KEY = 'ludipek:selected_location';
  // ?sucursal=CODE on a staff view link (STAFF_VIEW_STORE_QUERY_PARAMETER in the API's constants.py).
  const STAFF_VIEW_STORE_PARAMETER = 'sucursal';
  const STAFF_VIEW_ATTEMPT_KEY = 'ludipek:staff_view_switch';
  const STAFF_VIEW_ATTEMPT_WINDOW_MS = 30000;
  const CONTAINER_ID = 'b2b-location-selector';

  // Canonical city names live in STORE_CONFIGS[].display_name (ludipek_api/constants.py) — this is the
  // theme mirror (JS can't import Python); keep the labels in sync when adding a market. STORE_DISPLAY
  // drives dropdown labels (city + flag); STORE_COUNTRY and STORE_CURRENCY let other scripts derive the
  // effective country/currency from the selected store_code (instead of relying on BOOTSTRAP.storeCountry,
  // which reflects the Shopify Market, not the B2B location).
  const STORE_DISPLAY = {
    SCL: { label: 'Santiago', flag: '🇨🇱' },
    CCP: { label: 'Concepción', flag: '🇨🇱' },
    ZCO: { label: 'Temuco', flag: '🇨🇱' },
    PER: { label: 'Lima', flag: '🇵🇪' },
    TRU: { label: 'Trujillo', flag: '🇵🇪' },
    PIU: { label: 'Piura', flag: '🇵🇪' },
  };

  const STORE_COUNTRY = {
    SCL: 'CL', CCP: 'CL', ZCO: 'CL',
    PER: 'PE', TRU: 'PE', PIU: 'PE',
  };

  const STORE_CURRENCY = {
    SCL: 'CLP', CCP: 'CLP', ZCO: 'CLP',
    PER: 'PEN', TRU: 'PEN', PIU: 'PEN',
  };

  // <optgroup> labels when one company has branches in both countries (rare — a company is keyed by
  // one tax id — but the test company has all six, and a flat list mixing CLP and PEN reads as one market).
  const COUNTRY_GROUP_LABEL = {
    CL: 'Chile · CLP',
    PE: 'Perú · PEN',
  };

  function displayLabelFor(storeCode) {
    const entry = STORE_DISPLAY[storeCode];
    if (entry) return `${entry.flag} ${entry.label}`;
    // Fallback if a new market lands in companyLocations before this map is updated — show
    // the raw store_code rather than empty parens. Surfaces the gap without breaking the UI.
    return storeCode;
  }

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-location-selector]', message);
    } else {
      console.debug('[b2b-location-selector]', message, details);
    }
  }

  function logWarn(message, details) {
    if (details === undefined) {
      console.warn('[b2b-location-selector]', message);
    } else {
      console.warn('[b2b-location-selector]', message, details);
    }
  }

  function readSelection() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.store_code === 'string') return parsed;
      return null;
    } catch (error) {
      logDebug('failed to read stored location', { error: String(error) });
      return null;
    }
  }

  function writeSelection(location) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } catch (error) {
      logWarn('failed to persist location selection', { error: String(error) });
    }
  }

  function availableLocations() {
    if (BOOTSTRAP === null || !Array.isArray(BOOTSTRAP.companyLocations)) return [];
    // Only locations with a real netsuite_customer_id can drive pricing — anything else is
    // pending-state noise that the body.b2b-pending CSS gate already hides.
    return BOOTSTRAP.companyLocations.filter(
      (location) => location && typeof location.store_code === 'string' && location.netsuite_customer_id != null,
    );
  }

  function getStoreCode() {
    const selection = readSelection();
    return selection ? selection.store_code : null;
  }

  function getLocation() {
    return readSelection();
  }

  function getCurrentNetsuiteCustomerId() {
    const selected = getStoreCode();
    if (selected == null) return null;
    const match = availableLocations().find((location) => location.store_code === selected);
    return match ? match.netsuite_customer_id : null;
  }

  function getEffectiveCountry() {
    const code = getStoreCode();
    if (code && STORE_COUNTRY[code]) return STORE_COUNTRY[code];
    return BOOTSTRAP ? BOOTSTRAP.storeCountry : null;
  }

  function getEffectiveCurrency() {
    const code = getStoreCode();
    if (code && STORE_CURRENCY[code]) return STORE_CURRENCY[code];
    return BOOTSTRAP ? BOOTSTRAP.storeCurrency : null;
  }

  function selectorLabel() {
    const messages = BOOTSTRAP && BOOTSTRAP.messages;
    return (messages && messages.locationSelectorLabel) || 'Mis Locales';
  }

  function cityNameFor(storeCode) {
    // Plain city name (no flag) for the accessible name — flag emoji read poorly to screen
    // readers. Falls back to the raw store_code for markets not yet in STORE_DISPLAY.
    const entry = STORE_DISPLAY[storeCode];
    return entry ? entry.label : storeCode;
  }

  function renderSelector(container, locations, currentCode) {
    // Visible pill — reflects the currently selected city (flag + name). The native <select>
    // below covers it transparently, so clicks, keyboard, and mobile pickers all keep their
    // native behavior. The pill is aria-hidden; the <select> below carries the accessible name.
    const pill = document.createElement('span');
    pill.className = 'b2b-location-label';
    pill.setAttribute('aria-hidden', 'true');
    const cityText = document.createElement('span');
    cityText.className = 'b2b-location-label__city';
    // The currency the branch prices in, so the market is explicit before the first price renders
    // ("Santiago · CLP"). Shown at every width: phones need the market context as much as desktop,
    // and the pill has its own row on phones (see the followup: theme-market block of ludipek-brand.css).
    const currencyText = document.createElement('span');
    currencyText.className = 'b2b-location-label__currency';
    const caret = document.createElement('span');
    caret.className = 'b2b-location-label__caret';
    caret.textContent = '▾';
    pill.appendChild(cityText);
    pill.appendChild(currencyText);
    pill.appendChild(caret);

    const select = document.createElement('select');
    select.className = 'b2b-location-select b2b-location-select--overlay';

    function applyLabel(storeCode) {
      // Keep the visible pill and the <select>'s accessible name in sync with the active city.
      const currency = STORE_CURRENCY[storeCode];
      cityText.textContent = displayLabelFor(storeCode);
      currencyText.textContent = currency ? `· ${currency}` : '';
      const currencySuffix = currency ? ` (${currency})` : '';
      select.setAttribute('aria-label', `${selectorLabel()}: ${cityNameFor(storeCode)}${currencySuffix}`);
    }
    applyLabel(currentCode);

    const countries = new Set(locations.map((location) => STORE_COUNTRY[location.store_code] || ''));
    const groupByCountry = countries.size > 1;
    const groups = {};
    locations.forEach((location) => {
      const option = document.createElement('option');
      option.value = location.store_code;
      option.textContent = displayLabelFor(location.store_code);
      if (location.store_code === currentCode) {
        option.selected = true;
      }
      const country = STORE_COUNTRY[location.store_code] || '';
      if (!groupByCountry) {
        select.appendChild(option);
        return;
      }
      if (!groups[country]) {
        groups[country] = document.createElement('optgroup');
        groups[country].label = COUNTRY_GROUP_LABEL[country] || country || 'Otras';
        select.appendChild(groups[country]);
      }
      groups[country].appendChild(option);
    });

    select.addEventListener('change', () => {
      const chosen = locations.find((loc) => loc.store_code === select.value);
      if (!chosen) return;
      // Update the visible label immediately so the pill reflects the choice even if the
      // set_current_url navigation below is momentarily slow.
      applyLabel(chosen.store_code);
      writeSelection(chosen);
      if (window.LudipekB2BCache) {
        window.LudipekB2BCache.invalidateAll();
      }
      // Switch Shopify's *native* B2B company location too, not just our pricing flag — otherwise
      // native checkout keeps the buyer's default location (e.g. SCL) and ships/routes from there
      // while we priced for the selected one. `set_current_url` (Liquid `url_to_set_as_current`)
      // updates the session's active company location server-side and returns to this page, so the
      // reload below comes for free. Falls back to a plain reload if the URL is missing.
      if (chosen.set_current_url) {
        logDebug('location changed, switching Shopify company location', { store_code: chosen.store_code });
        window.location.href = chosen.set_current_url;
        return;
      }
      logWarn('location has no set_current_url — reloading without switching company location', {
        store_code: chosen.store_code,
      });
      window.location.reload();
    });

    container.innerHTML = '';
    container.appendChild(pill);
    container.appendChild(select);
    container.style.display = '';
  }

  function serverActiveLocation(locations) {
    // Shopify's session-active company location (customer.current_location, surfaced as
    // BOOTSTRAP.currentLocation) is the source of truth. The native post-login chooser — and our own
    // set_current_url navigation — set it server-side but never touch our localStorage flag, so we
    // reconcile to it on load. Returns null when there is no active location, or when the active one
    // is not pricing-capable (filtered out of availableLocations), in which case we keep localStorage.
    if (BOOTSTRAP === null || BOOTSTRAP.currentLocation == null) return null;
    return locations.find((location) => location.store_code === BOOTSTRAP.currentLocation) || null;
  }

  // The discount admin's "Ver tienda como este cliente" opens the store with ?sucursal=CODE after pointing the team
  // member's location for that store at the customer. Switch the session to that location once (set_current_url
  // returns to this page, where the location is then active); a failed switch is not retried, so it cannot loop.
  // Only a staff view honours it: a buyer's link with the parameter changes nothing.
  function switchToRequestedStaffLocation(locations, serverLocation) {
    if (BOOTSTRAP.staffView !== true) return false;
    const url = new URL(window.location.href);
    const requested = (url.searchParams.get(STAFF_VIEW_STORE_PARAMETER) || '').toUpperCase();
    if (!requested) return false;
    url.searchParams.delete(STAFF_VIEW_STORE_PARAMETER);
    const target = locations.find((location) => location.store_code === requested);
    // An attempt counts for a short while only: set_current_url may come back without the parameter, which would
    // otherwise leave the flag behind and make a later link for the same store do nothing.
    const attemptKey = `${STAFF_VIEW_ATTEMPT_KEY}:${requested}`;
    let attempted = false;
    try {
      const attemptedAt = Number(sessionStorage.getItem(attemptKey));
      attempted = attemptedAt > 0 && Date.now() - attemptedAt < STAFF_VIEW_ATTEMPT_WINDOW_MS;
    } catch (error) {
      attempted = true;
    }
    if (!target || (serverLocation && serverLocation.store_code === requested) || attempted || !target.set_current_url) {
      try {
        sessionStorage.removeItem(attemptKey);
      } catch (error) {
        // Storage blocked: nothing to clean up.
      }
      window.history.replaceState(window.history.state, '', url.toString());
      return false;
    }
    try {
      sessionStorage.setItem(attemptKey, String(Date.now()));
    } catch (error) {
      return false;
    }
    writeSelection(target);
    if (window.LudipekB2BCache) window.LudipekB2BCache.invalidateAll();
    logDebug('staff view: switching to the requested store', { store_code: requested });
    window.location.href = target.set_current_url;
    return true;
  }

  function initialize() {
    if (BOOTSTRAP === null || !BOOTSTRAP.isLoggedIn || BOOTSTRAP.isPending === true) {
      return;
    }

    const locations = availableLocations();
    if (locations.length === 0) {
      logDebug('no locations available for customer');
      return;
    }

    const container = document.getElementById(CONTAINER_ID);
    const serverLocation = serverActiveLocation(locations);
    if (switchToRequestedStaffLocation(locations, serverLocation)) return;
    const existing = readSelection();

    // Reconcile localStorage to the server's active location first, so a native chooser selection
    // sticks instead of silently resetting to the first available location on the next page load.
    if (serverLocation && (!existing || existing.store_code !== serverLocation.store_code)) {
      writeSelection(serverLocation);
      if (window.LudipekB2BCache) {
        window.LudipekB2BCache.invalidateAll();
      }
      logDebug('synced selection to Shopify active location', { store_code: serverLocation.store_code });
    }

    if (locations.length === 1) {
      // Single-location buyer: auto-select silently (no dropdown to render).
      if (!serverLocation && (!existing || existing.store_code !== locations[0].store_code)) {
        writeSelection(locations[0]);
        logDebug('auto-selected single location', { store_code: locations[0].store_code });
      }
      return;
    }

    // Multi-location buyer: server active location wins; otherwise keep a still-valid stored
    // selection; otherwise default to the first location. Then render the dropdown.
    const stored = readSelection();
    const validStored = stored ? locations.find((loc) => loc.store_code === stored.store_code) : null;
    const current = serverLocation || validStored || locations[0];
    if (!serverLocation && !validStored) {
      writeSelection(current);
    }
    if (container) {
      renderSelector(container, locations, current.store_code);
    } else {
      logWarn('selector container #b2b-location-selector not found in DOM');
    }
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  // Expose API synchronously so other scripts can read the location immediately.
  window.LudipekB2BLocation = {
    getStoreCode,
    getLocation,
    getCurrentNetsuiteCustomerId,
    getEffectiveCountry,
    getEffectiveCurrency,
  };

  onReady(initialize);
})();
